import React, { useEffect, useMemo, useState } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";
import Swal from "sweetalert2";
import { upsertCommissionInquiryFees } from "../commissionsApi";
import { sanitizeDecimalInput } from "../../../../utilis/invoiceLineItemHelpers";
import { buildInquiryFeeLedgerSections } from "./inquiryFeeInvoiceLedger";
import {
  buildPlanPaymentSummaryViewModel,
  resolvePlanPaymentSummary,
} from "./commissionPlanSummaryUtils";

const SECTION_ACCENTS = {
  tuition: {
    border: "border-l-slate-400",
    header: "bg-slate-50 text-slate-800",
    pill: "bg-slate-100 text-slate-700",
  },
  discount: {
    border: "border-l-violet-400",
    header: "bg-violet-50/80 text-violet-900",
    pill: "bg-violet-100 text-violet-800",
  },
  commission: {
    border: "border-l-[#007BFF]",
    header: "bg-[#F0F7FF] text-[#007BFF]",
    pill: "bg-[#007BFF]/10 text-[#007BFF]",
  },
  bonus: {
    border: "border-l-amber-400",
    header: "bg-amber-50 text-amber-900",
    pill: "bg-amber-100 text-amber-800",
  },
  inquiry_fees: {
    border: "border-l-teal-500",
    header: "bg-teal-50/80 text-teal-900",
    pill: "bg-teal-100 text-teal-800",
  },
  application_fee: {
    border: "border-l-teal-500",
    header: "bg-teal-50/80 text-teal-900",
    pill: "bg-teal-100 text-teal-800",
  },
  material_fee: {
    border: "border-l-cyan-500",
    header: "bg-cyan-50/80 text-cyan-900",
    pill: "bg-cyan-100 text-cyan-800",
  },
};

function sectionToDisplayColumn(section) {
  return {
    id: section.id,
    title: section.title,
    lines: (section.rows || []).map((row, index) => ({
      step: index + 1,
      label: row.label,
      type: row.type,
      highlight: Boolean(row.highlight),
      amount: row.amount,
      sublabel: row.sublabel,
      status: row.status,
      statusTone: row.statusTone,
    })),
  };
}

function splitInquiryColumnLines(lines = []) {
  const matIndex = lines.findIndex((line) =>
    String(line.label || "").startsWith("Material fee"),
  );
  if (matIndex < 0) {
    return { application_fee: lines, material_fee: [] };
  }
  return {
    application_fee: lines.slice(0, matIndex),
    material_fee: lines.slice(matIndex),
  };
}

/** Split combined inquiry column into Application fee + Material fee cards. */
function buildDisplaySummaryCards(
  columns = [],
  { inquiryFeeLedgerSections, inquiryFeesReadOnly } = {},
) {
  const cards = [];

  columns.forEach((column) => {
    if (column.id !== "inquiry_fees") {
      cards.push(column);
      return;
    }

    if (
      inquiryFeesReadOnly &&
      Array.isArray(inquiryFeeLedgerSections) &&
      inquiryFeeLedgerSections.length
    ) {
      inquiryFeeLedgerSections.forEach((section) => {
        cards.push(sectionToDisplayColumn(section));
      });
      return;
    }

    const { application_fee, material_fee } = splitInquiryColumnLines(
      column.lines || [],
    );
    if (application_fee.length) {
      cards.push({
        id: "application_fee",
        title: "Application fee",
        lines: application_fee,
      });
    }
    if (material_fee.length) {
      cards.push({
        id: "material_fee",
        title: "Material fee",
        lines: material_fee,
      });
    }
    if (!application_fee.length && !material_fee.length) {
      cards.push(column);
    }
  });

  return cards;
}

function resolveSectionBalance(column) {
  if (column?.balanceAmount) {
    return {
      label: column.balanceLabel || "Balance",
      amount: column.balanceAmount,
    };
  }
  const lines = column?.lines || [];
  const totalRow =
    lines.find((line) => line.type === "total" || line.highlight) ||
    lines[lines.length - 1];
  if (!totalRow?.amount) return null;
  return {
    label: totalRow.label || "Balance",
    amount: totalRow.amount,
  };
}

const inputClass =
  "h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px] font-medium text-slate-800 outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20";

function prefixForType(type) {
  if (type === "deduct") return "−";
  if (type === "total" || type === "subtotal") return "=";
  return "";
}

function amountClass(type, highlight) {
  if (type === "deduct") return "text-rose-600";
  if (type === "total" || highlight) return "font-bold text-[#007BFF]";
  if (type === "subtotal") return "font-semibold text-slate-900";
  return "font-semibold text-slate-800";
}

function LedgerAmount({ value, type, highlight }) {
  const prefix = prefixForType(type);
  const cls = amountClass(type, highlight);
  if (!value) return <span className="text-slate-300">—</span>;
  return (
    <span className={`shrink-0 text-[13px] tabular-nums leading-tight ${cls}`}>
      {prefix ? `${prefix} ` : ""}
      {value}
    </span>
  );
}

function InquiryFeesPaidEditor({
  applicationFeePlanned,
  materialFeePlanned,
  applicationFeePaid,
  materialFeePaid,
  onApplicationFeePaidChange,
  onMaterialFeePaidChange,
  onSave,
  onCancel,
  saving,
}) {
  const showApplication = Number(applicationFeePlanned) > 0;
  const showMaterial = Number(materialFeePlanned) > 0;

  return (
    <div className="flex flex-1 flex-col gap-3 border-t border-slate-100 p-3">
      <p className="mb-0 text-[11px] leading-snug text-slate-500">
        Record paid amounts for your files — not linked to invoices.
      </p>
      {showApplication ? (
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Application fee paid
          </span>
          <input
            type="text"
            inputMode="decimal"
            className={inputClass}
            value={applicationFeePaid}
            onChange={(event) =>
              onApplicationFeePaidChange(
                sanitizeDecimalInput(event.target.value),
              )
            }
            placeholder="0.00"
          />
        </label>
      ) : null}
      {showMaterial ? (
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Material fee paid
          </span>
          <input
            type="text"
            inputMode="decimal"
            className={inputClass}
            value={materialFeePaid}
            onChange={(event) =>
              onMaterialFeePaidChange(sanitizeDecimalInput(event.target.value))
            }
            placeholder="0.00"
          />
        </label>
      ) : null}
      <div className="mt-auto flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
          onClick={onCancel}
          disabled={saving}
        >
          <FaTimes size={10} />
          Cancel
        </button>
        <button
          type="button"
          className="inline-flex h-8 items-center gap-1 rounded-md bg-[#007BFF] px-2.5 text-[11px] font-semibold text-white hover:bg-[#0069d9] disabled:opacity-60"
          onClick={onSave}
          disabled={saving}
        >
          <FaCheck size={10} />
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ label, tone = "slate" }) {
  const tones = {
    green: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    slate: "border-slate-200 bg-slate-50 text-slate-600",
  };
  if (!label || label === "—") return null;
  return (
    <span
      className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tones[tone] || tones.slate}`}
    >
      {label}
    </span>
  );
}

function LedgerStepRow({ line }) {
  const isTotal = line.type === "total" || line.highlight;
  const isSubtotal = line.type === "subtotal";
  const isDeduct = line.type === "deduct";

  return (
    <div
      className={`flex items-start justify-between gap-3 py-2 ${
        isTotal
          ? "mt-1 border-t-2 border-slate-200/80 pt-3"
          : isSubtotal
            ? "border-t border-dashed border-slate-200"
            : ""
      }`}
    >
      <div className="min-w-0 flex-1">
        <p
          className={`mb-0 text-[12px] leading-snug ${
            isTotal
              ? "font-semibold text-slate-900"
              : isDeduct
                ? "text-slate-500"
                : "text-slate-700"
          }`}
        >
          {line.label}
        </p>
        {line.sublabel ? (
          <p className="mb-0 mt-0.5 text-[11px] text-slate-500">{line.sublabel}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        {line.status ? (
          <StatusBadge label={line.status} tone={line.statusTone} />
        ) : null}
        <LedgerAmount
          value={line.amount}
          type={line.type}
          highlight={line.highlight}
        />
      </div>
    </div>
  );
}

function SummarySectionCard({ column, inquiryFeesEdit }) {
  const accent = SECTION_ACCENTS[column.id] || SECTION_ACCENTS.tuition;
  const isCombinedInquiry = column.id === "inquiry_fees";
  const editing = isCombinedInquiry && inquiryFeesEdit?.editing;
  const balance = resolveSectionBalance(column);

  return (
    <article
      className={`flex h-full flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm border-l-4 ${accent.border}`}
    >
      <header
        className={`flex flex-wrap items-center justify-between gap-2 px-4 py-3 ${accent.header}`}
      >
        <h5 className="mb-0 text-[13px] font-semibold">{column.title}</h5>
        {balance ? (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold tabular-nums ${accent.pill}`}
          >
            {balance.label}: {balance.amount}
          </span>
        ) : null}
      </header>

      <div className="flex flex-1 flex-col px-4 pb-4 pt-1">
        {editing ? (
          <InquiryFeesPaidEditor {...inquiryFeesEdit} />
        ) : column.lines?.length ? (
          <div className="flex flex-col">
            {column.lines.map((line) => (
              <LedgerStepRow
                key={`${column.id}-${line.step}-${line.label}`}
                line={line}
              />
            ))}
          </div>
        ) : (
          <p className="mb-0 py-2 text-[12px] text-slate-400">No amounts yet.</p>
        )}
      </div>
    </article>
  );
}

function CommissionPaymentSummaryCard({
  apiSummary = null,
  installments = [],
  plan = null,
  currency = "AUD",
  bonus = null,
  inquiryFeeRecord = null,
  commissionInvoices = [],
  inquiryFeesReadOnly = false,
  inquiryFeeKeys = null,
  onInquiryFeesPaidSaved,
}) {
  const [editingInquiryFees, setEditingInquiryFees] = useState(false);
  const [savingInquiryFees, setSavingInquiryFees] = useState(false);
  const [applicationFeePaid, setApplicationFeePaid] = useState("");
  const [materialFeePaid, setMaterialFeePaid] = useState("");

  const resolved = useMemo(
    () =>
      resolvePlanPaymentSummary({
        apiSummary,
        installments,
        plan,
      }),
    [apiSummary, installments, plan],
  );

  const model = useMemo(
    () =>
      buildPlanPaymentSummaryViewModel(resolved, plan, currency, {
        bonus,
        installments,
        commissionInvoices,
      }),
    [resolved, plan, currency, bonus, installments, commissionInvoices],
  );

  const hasInquiryFeesColumn = model?.ledger?.columns?.some(
    (column) => column.id === "inquiry_fees",
  );

  const canEditInquiryFees =
    !inquiryFeesReadOnly &&
    Boolean(inquiryFeeKeys?.applicationId) &&
    Boolean(inquiryFeeKeys?.clientId) &&
    Boolean(inquiryFeeKeys?.providerId) &&
    hasInquiryFeesColumn;

  const inquiryFeeLedgerSections = useMemo(() => {
    if (!inquiryFeesReadOnly || !hasInquiryFeesColumn) return null;
    return buildInquiryFeeLedgerSections(
      commissionInvoices,
      inquiryFeeRecord,
      currency,
    );
  }, [
    inquiryFeesReadOnly,
    hasInquiryFeesColumn,
    commissionInvoices,
    inquiryFeeRecord,
    currency,
  ]);

  useEffect(() => {
    if (!editingInquiryFees) {
      setApplicationFeePaid(
        inquiryFeeRecord?.application_fee_paid != null &&
          inquiryFeeRecord?.application_fee_paid !== ""
          ? String(inquiryFeeRecord.application_fee_paid)
          : "",
      );
      setMaterialFeePaid(
        inquiryFeeRecord?.material_fee_paid != null &&
          inquiryFeeRecord?.material_fee_paid !== ""
          ? String(inquiryFeeRecord.material_fee_paid)
          : "",
      );
    }
  }, [inquiryFeeRecord, editingInquiryFees]);

  const handleSaveInquiryFeesPaid = async () => {
    if (!canEditInquiryFees) return;

    setSavingInquiryFees(true);
    try {
      const saved = await upsertCommissionInquiryFees({
        application_id: inquiryFeeKeys.applicationId,
        client_id: inquiryFeeKeys.clientId,
        provider_id: inquiryFeeKeys.providerId,
        application_course_id: inquiryFeeKeys.applicationCourseId,
        application_fee: inquiryFeeRecord?.application_fee ?? 0,
        material_fee: inquiryFeeRecord?.material_fee ?? 0,
        application_fee_paid: applicationFeePaid,
        material_fee_paid: materialFeePaid,
        currency: inquiryFeeRecord?.currency || currency || "AUD",
      });
      setEditingInquiryFees(false);
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Fee paid amounts updated.",
        timer: 1400,
        showConfirmButton: false,
      });
      if (typeof onInquiryFeesPaidSaved === "function") {
        await onInquiryFeesPaidSaved(saved);
      }
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to save fee paid amounts.",
        "error",
      );
    } finally {
      setSavingInquiryFees(false);
    }
  };

  const inquiryFeesEdit = canEditInquiryFees
    ? {
        canEdit: true,
        editing: editingInquiryFees,
        onStart: () => setEditingInquiryFees(true),
        onCancel: () => setEditingInquiryFees(false),
        onSave: handleSaveInquiryFeesPaid,
        saving: savingInquiryFees,
        applicationFeePlanned: inquiryFeeRecord?.application_fee ?? 0,
        materialFeePlanned: inquiryFeeRecord?.material_fee ?? 0,
        applicationFeePaid,
        materialFeePaid,
        onApplicationFeePaidChange: setApplicationFeePaid,
        onMaterialFeePaidChange: setMaterialFeePaid,
      }
    : null;

  const columns = model?.ledger?.columns;
  const displayCards = useMemo(
    () =>
      buildDisplaySummaryCards(columns || [], {
        inquiryFeeLedgerSections,
        inquiryFeesReadOnly,
      }),
    [columns, inquiryFeeLedgerSections, inquiryFeesReadOnly],
  );

  if (!displayCards.length) return null;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3.5">
        <h4 className="mb-0 text-[15px] font-semibold text-slate-900">
          Payment summary
        </h4>
      </div>

      <div className="cms-commission-summary-grid p-4">
        {displayCards.map((column) => (
          <SummarySectionCard
            key={column.id}
            column={column}
            inquiryFeesEdit={
              column.id === "inquiry_fees" ? inquiryFeesEdit : null
            }
          />
        ))}
      </div>
    </div>
  );
}

export default CommissionPaymentSummaryCard;
